import * as React from "react";
import { useEffect, useState } from "react";
import { Switch, Route, useHistory } from "react-router-dom";
import "../taskpane.css";
import { useAppController } from "../hooks/useAppController";
import DashboardRoute from "./routes/DashboardRoute";
import AsinSkuFilterRoute from "./routes/AsinSkuFilterRoute";
import CustomDateRangeRoute from "./routes/CustomDateRangeRoute";
import EditPromptRoute from "./routes/EditPromptRoute";
import KeywordIntelligenceRoute from "./routes/KeywordIntelligenceRoute";
import AttributionAnalysisRoute from "./routes/AttributionAnalysisRoute";
import CampaignPerformanceRoute from "./routes/CampaignPerformanceRoute";
import PreserveComputationRoute from "./routes/PreserveComputationRoute";
import SalesFilterRoute from "./routes/SalesFilterRoute";
import { Text } from "@fluentui/react-components";
import LoginRoute from "./routes/LoginRoute";
import ProtectedRoute from "./routes/ProtectedRoute";
import TemplatesList from "./templates/TemplatesList";
import SaveTemplatesRoute from "./routes/SaveTemplatesRoute";
import SmartMatchRoute from "./routes/SmartMatchRoute";
import SmartColumnRoute from "./routes/SmartColumnRoute";
import PivotTableRoute from "./routes/PivotTableRoute";
import RootCauseAnalysisRoute from "./routes/RootCauseAnalysisRoute";
import MarketWatchRoute from "./routes/MarketWatchRoute";
import AIInsightsRoute from "./routes/ai-insights-route";
import ConfigurationSettingsRoute from "./routes/ConfigurationSettingsRoute";
import SpreetailLoader from "./common/SpreetailLoader";
import { STORAGE_KEYS, POLLING } from "../../shared/constants";

interface AppProps { title: string; }

const App: React.FC<AppProps> = () => {
  const controller = useAppController();
  const history = useHistory();
  const [externalGenerating, setExternalGenerating] = useState<boolean>(false);
  const [externalGeneratingName, setExternalGeneratingName] = useState<string>("");

  useEffect(() => {
    let mounted = true;
    let timer: any;
    const tick = async () => {
      try {
        const flag = await OfficeRuntime.storage.getItem(STORAGE_KEYS.DASHBOARD_GENERATING);
        const name = await OfficeRuntime.storage.getItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME);
        const navigateToSave = await OfficeRuntime.storage.getItem(STORAGE_KEYS.NAVIGATE_TO_SAVE_TEMPLATES);
        
        if (!mounted) return;
        setExternalGenerating(flag === "true");
        setExternalGeneratingName(name || "");
        
        // Check if we need to navigate to save templates
        if (navigateToSave === "true") {
          await OfficeRuntime.storage.removeItem(STORAGE_KEYS.NAVIGATE_TO_SAVE_TEMPLATES);
          history.push("/save-templates");
        }
      } catch (_) {
        // ignore
      } finally {
        timer = setTimeout(tick, POLLING.TOKEN_CHECK_INTERVAL_MS);
      }
    };
    tick();
    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [history]);

  return (
    <div className="taskpane-container" style={{ display: "flex", flexDirection: "column", minHeight: "100vh", overflow: "hidden", fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial" }}>
      <Switch>
        <ProtectedRoute exact path="/" component={DashboardRoute} />
        <ProtectedRoute path="/asin-filter" component={AsinSkuFilterRoute} />
        <ProtectedRoute path="/custom-date-range" component={CustomDateRangeRoute} />
        <ProtectedRoute path="/edit-prompt" component={EditPromptRoute} />
        <ProtectedRoute path="/campaign-performance" component={CampaignPerformanceRoute} />
        <ProtectedRoute path="/preserve-computation" component={PreserveComputationRoute} />
        <ProtectedRoute path="/sales-filter" component={SalesFilterRoute} />
        <ProtectedRoute path="/keyword-intelligence" component={KeywordIntelligenceRoute} />
        <ProtectedRoute path="/attribution-analysis" component={AttributionAnalysisRoute} />
        <ProtectedRoute path="/templates" component={TemplatesList} />
        <ProtectedRoute path="/save-templates" component={SaveTemplatesRoute} />
        <ProtectedRoute path="/smart-match" component={SmartMatchRoute} />
        <ProtectedRoute path="/smart-column" component={SmartColumnRoute} />
        <ProtectedRoute path="/pivot-table" component={PivotTableRoute} />
        <ProtectedRoute path="/root-cause-analysis" component={RootCauseAnalysisRoute} />
        <ProtectedRoute path="/market-watch" component={MarketWatchRoute} />
        <ProtectedRoute path="/ai-insights" component={AIInsightsRoute} />
        <ProtectedRoute path="/configuration-settings" component={ConfigurationSettingsRoute} />
        <Route path="/login" component={LoginRoute} />
      </Switch>

      {(controller.isGeneratingDashboard || externalGenerating) && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            background:
              "linear-gradient(180deg, rgba(211,232,217,0.95) 0%, rgba(238,238,238,0.95) 79.45%)",
            backdropFilter: "blur(6px)",
            zIndex: 2000,
          }}
        >
          <SpreetailLoader
            title="Working on your data"
            subtitle="Please wait while we generate your data"
            size="large"
          />
        </div>
      )}
    </div>
  );
};

export default App;